/**
 * The deck: what is in it, how it is dealt, and how a card id becomes a car
 * (SPEC-011 PR-11.1).
 *
 * A match holds IDS rather than records (SPEC-011 D-1), for the reason
 * `application/game/collection.js` already gives about the same catalogue: a
 * copy of a car freezes figures that `carSpecs.js` exists to keep correcting,
 * and a reader who started a match in August should not be playing a Supra with
 * August's horsepower on it. It also keeps a match state small enough for a test
 * to assert on whole, and — the reason that matters past this slice — small
 * enough to be a document.
 *
 * So every rule function takes a DECK alongside the match, and the deck is the
 * one place an id becomes a car. Nothing else in `domain/game` does that lookup.
 *
 * Pure. `random` is a defaulted parameter for the same reason it is one in
 * `carDuel`: a module that reaches for `Math.random` internally cannot be asked
 * what it does with a given draw, and every test below is built on asking it.
 */

/**
 * Five cards each, which is the number the game is remembered at.
 *
 * It is also the number that makes fourteen cars a match rather than a deal:
 * five and five leaves four in the stock, which is one refill and a bit, and
 * four or five rounds before the hands run out. A hand of seven would deal the
 * whole catalogue to two players and leave nothing face-down, which removes the
 * only hidden information in the game — with the full deck known, each player
 * could name the other's hand exactly.
 */
export const HAND_SIZE = 5;

/** The two sides of a match, in the order they are drawn on the table. */
export const SIDES = ['player', 'opponent'];

/** The other one. Written once because a typo here is a silent rule change. */
export function otherSide(side) {
    return side === 'player' ? 'opponent' : 'player';
}

/**
 * The lookup a match is read through.
 *
 * A Map rather than an object, because a car id is arbitrary content and an
 * object would give every one of them a prototype's worth of keys to collide
 * with — `constructor` is not a car, and `byId.constructor` answering a function
 * is exactly the kind of thing that turns into a crash three modules away.
 */
export function buildDeck(cars) {
    const catalogue = Array.isArray(cars) ? cars : [];

    return new Map(
        catalogue
            .filter((car) => typeof car?.id === 'string')
            .map((car) => [car.id, car]),
    );
}

/** The car behind a card id, or null where the deck does not carry it. */
export function cardOf(deck, id) {
    return deck?.get?.(id) ?? null;
}

/** Every card in a list, as cars, skipping anything the deck cannot resolve. */
export function cardsOf(deck, ids) {
    return (Array.isArray(ids) ? ids : [])
        .map((id) => cardOf(deck, id))
        .filter(Boolean);
}

/**
 * A shuffled copy, by Fisher–Yates.
 *
 * The index is clamped rather than wrapped, because a `random` that answers
 * exactly 1 — which a test double is entitled to do, and which is the draw a
 * suite reaches for when it wants "the last one" — would otherwise index past
 * the end and swap a card with `undefined`. That defect does not throw. It
 * deletes a car from the deck and puts a hole in a hand, several moves later.
 */
export function shuffle(ids, { random = Math.random } = {}) {
    const order = [...(Array.isArray(ids) ? ids : [])];

    for (let index = order.length - 1; index > 0; index -= 1) {
        const swap = Math.min(Math.floor(random() * (index + 1)), index);
        const held = order[index];

        order[index] = order[swap];
        order[swap] = held;
    }

    return order;
}

/**
 * Deal a match, or answer null where this catalogue cannot hold one.
 *
 * Null rather than a short deal. A match dealt from nine cars would give one
 * side four and no stock, and a game whose rules quietly change with the size of
 * the reader's collection is a game nobody can learn — SPEC-010 means the
 * collection grows, so this would be a different game at twelve cars and at
 * fourteen. The screen draws a designed state for null, exactly as the duel does
 * for a pool it cannot deal from.
 *
 * The stock is what is left, and it is deliberately left rather than trimmed to
 * a round number: four face-down cards is one refill and a bit, and every card
 * the catalogue grows by lengthens the match instead of being discarded.
 */
export function dealMatch(ids, { random = Math.random, handSize = HAND_SIZE } = {}) {
    const order = shuffle(ids, { random });

    if (order.length < handSize * SIDES.length) {
        return null;
    }

    return {
        hands: {
            player: order.slice(0, handSize),
            opponent: order.slice(handSize, handSize * 2),
        },
        stock: order.slice(handSize * 2),
    };
}

/**
 * Take `count` cards off the top of the stock.
 *
 * Answers both halves, because every caller needs both and computing the
 * remainder at the call site is how a card gets dealt twice. Asking for more
 * than the stock holds is not an error: a stock that has run out is the normal
 * ending of a match, not a failure inside one.
 */
export function drawFromStock(stock, count) {
    const available = Array.isArray(stock) ? stock : [];
    const taken = Math.max(0, Math.min(count, available.length));

    return { drawn: available.slice(0, taken), stock: available.slice(taken) };
}

/**
 * Refill a hand to `handSize` from the stock.
 *
 * A hand already at or over the size draws nothing, which is not a guard against
 * an impossible state: the doubling rule moves a card from one hand to the other
 * player's POT rather than their hand (SPEC-011 D-10), but a future rule that
 * moved it into a hand would put a player over the size, and silently drawing
 * a negative number of cards is the shape of bug that survives a suite.
 */
export function refillHand(hand, stock, { handSize = HAND_SIZE } = {}) {
    const held = Array.isArray(hand) ? hand : [];
    const { drawn, stock: remaining } = drawFromStock(stock, handSize - held.length);

    return { hand: [...held, ...drawn], stock: remaining };
}
